import type {CapturedNetworkRequest} from 'posthog-js';

const REDACTED = '[REDACTED]';

const isVisibleEmptyValue = (value: unknown) => {
  if (value === null || value === '') return true;
  if (typeof value !== 'string') return false;

  const normalized = value.trim().toUpperCase();
  return normalized === 'NA' || normalized === 'N/A';
};

const maskValue = (value: unknown): unknown => {
  if (isVisibleEmptyValue(value)) return value;
  if (Array.isArray(value)) return value.map(maskValue);
  if (value && typeof value === 'object') {
    return Object.entries(value).reduce<Record<string, unknown>>(
      (masked, [key, nestedValue]) => {
        masked[key] = maskValue(nestedValue);
        return masked;
      },
      {}
    );
  }
  return REDACTED;
};

const maskBody = (body?: string | null) => {
  if (body === undefined || body === null || isVisibleEmptyValue(body)) {
    return body;
  }

  try {
    return JSON.stringify(maskValue(JSON.parse(body)));
  } catch {
    return REDACTED;
  }
};

const maskHeaders = (headers?: Record<string, string>) => {
  if (!headers) return headers;

  return Object.entries(headers).reduce<Record<string, string>>(
    (masked, [key, value]) => {
      masked[key] = String(maskValue(value));
      return masked;
    },
    {}
  );
};

const maskUrlQuery = (url: string) => {
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.forEach((value, key) => {
      parsed.searchParams.set(
        key,
        isVisibleEmptyValue(value) ? value : REDACTED
      );
    });
    return parsed.toString();
  } catch {
    return url;
  }
};

const maskCapturedNetworkRequest = (
  request: CapturedNetworkRequest
): CapturedNetworkRequest => ({
  ...request,
  name: maskUrlQuery(request.name),
  requestHeaders: maskHeaders(request.requestHeaders),
  responseHeaders: maskHeaders(request.responseHeaders),
  requestBody: maskBody(request.requestBody),
  responseBody: maskBody(request.responseBody),
});

export default maskCapturedNetworkRequest;
